import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import PublicLayout from '@/components/hydlib/PublicLayout';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function stars(n) {
    const full = Math.max(0, Math.min(5, Math.round(n || 0)));
    return '★'.repeat(full) + '☆'.repeat(5 - full);
}
function isKidsCategories(cats) {
    return (cats || []).some(c => /children|kids|baby|toddler|picture book|middle grade|early reader|3-5 years|5-8 years|6-8 years|9-12 years/i.test(c));
}
function ymd(d) { return d.toISOString().split('T')[0]; }
function nextThursday(from = new Date()) {
    const now = new Date();
    const base = new Date(Math.max(new Date(from).getTime(), now.getTime()));
    const d = new Date(base);
    d.setHours(12, 0, 0, 0);
    d.setDate(d.getDate() + ((4 - d.getDay() + 7) % 7));
    // Same-day (Thursday) delivery orders close at 9 AM
    if (d.toDateString() === now.toDateString() && now.getHours() >= 9) d.setDate(d.getDate() + 7);
    return d;
}
function fmtDateLong(iso) {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return iso; }
}

export default function BookDetails() {
    const { bookId } = useParams();
    const { user, getAuthHeader } = useAuth();
    const navigate = useNavigate();

    const [book, setBook] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [availability, setAvailability] = useState(null);
    const [rentalPrice, setRentalPrice] = useState(null);
    const [inTbr, setInTbr] = useState(false);
    const [loading, setLoading] = useState(true);

    // Hold modal state
    const [holdOpen, setHoldOpen] = useState(false);
    const [requestedDate, setRequestedDate] = useState('');
    const [deliveryOption, setDeliveryOption] = useState('self_pickup');
    const [forChild, setForChild] = useState(false);
    const [deliveryAddress, setDeliveryAddress] = useState('');
    const [placing, setPlacing] = useState(false);

    // Review draft
    const [draft, setDraft] = useState('');
    const [draftStars, setDraftStars] = useState(5);
    const [posting, setPosting] = useState(false);

    const [msg, setMsg] = useState('');
    const [msgKind, setMsgKind] = useState('info'); // 'info' | 'error' | 'success'

    // Staff edit
    const [editOpen, setEditOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [libCard, setLibCard] = useState(null);
    const [similar, setSimilar] = useState([]);

    useEffect(() => {
        window.scrollTo(0, 0);
        axios.get(`${API}/books/${bookId}/similar`)
            .then(r => setSimilar(r.data || []))
            .catch(() => setSimilar([]));
    }, [bookId]);

    useEffect(() => {
        if (user?.role !== 'staff') return;
        axios.get(`${API}/books/${bookId}/library-card`, { headers: getAuthHeader() })
            .then(r => setLibCard(r.data))
            .catch(() => { /* ignore */ });
    }, [user, bookId, getAuthHeader]);

    const handleSaveBook = async (form) => {
        setSaving(true);
        try {
            const payload = {
                title: form.title.trim(),
                authors: form.authors.split(',').map(a => a.trim()).filter(Boolean),
                isbn: form.isbn.trim() || null,
                publisher: form.publisher.trim() || null,
                published_date: form.published_date.trim() || null,
                page_count: form.page_count ? parseInt(form.page_count, 10) : null,
                description: form.description.trim() || null,
                categories: form.categories.split(',').map(c => c.trim()).filter(Boolean),
                cover_image_url: form.cover_image_url.trim() || null,
                total_copies: parseInt(form.total_copies, 10) || 1,
                available_copies: parseInt(form.available_copies, 10) || 0,
                rental_price: form.rental_price === '' ? null : parseFloat(form.rental_price),
            };
            const res = await axios.put(`${API}/books/${bookId}`, payload, { headers: getAuthHeader() });
            setBook(res.data);
            setEditOpen(false);
            showMsg('Book updated.', 'success');
        } catch (e) {
            showMsg(e.response?.data?.detail || 'Could not update book.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteBook = async () => {
        if (!window.confirm(`Delete "${book?.title}" from the catalog? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/books/${bookId}`, { headers: getAuthHeader() });
            navigate('/catalog');
        } catch (e) {
            showMsg(e.response?.data?.detail || 'Could not delete book.', 'error');
        }
    };

    const isMember = user?.role === 'member' && user?.subscription_status === 'active';
    const isStaff = user?.role === 'staff';
    const canHold = !!user && (isMember || isStaff);
    const kids = book && isKidsCategories(book.categories);
    const price = rentalPrice?.price ?? (kids ? 100 : 200);
    const availableCount = book?.available_copies ?? 0;
    const totalCount = book?.total_copies ?? 0;
    const isAvailable = availableCount > 0;

    const fetchAvailability = useCallback(async (dateStr) => {
        try {
            let url = `${API}/books/${bookId}/availability`;
            if (dateStr) url += `?requested_date=${dateStr}`;
            const res = await axios.get(url, { headers: getAuthHeader() });
            setAvailability(res.data);
        } catch { /* no-op */ }
    }, [bookId, getAuthHeader]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const bookRes = await axios.get(`${API}/books/${bookId}`, { headers: getAuthHeader() });
                if (cancelled) return;
                setBook(bookRes.data);
                axios.get(`${API}/reviews/book/${bookId}`, { headers: getAuthHeader() })
                    .then(r => !cancelled && setReviews(r.data || []))
                    .catch(() => {});
                axios.get(`${API}/rentals/price/${bookId}`)
                    .then(r => !cancelled && setRentalPrice(r.data))
                    .catch(() => {});
                fetchAvailability();
                if (user) {
                    axios.get(`${API}/tbr/${bookId}/check`, { headers: getAuthHeader() })
                        .then(r => !cancelled && setInTbr(!!r.data?.in_tbr))
                        .catch(() => {});
                }
            } catch { /* book fetch failed */ }
            finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [bookId, user]);

    const showMsg = (text, kind = 'info') => { setMsg(text); setMsgKind(kind); };

    const handleTbr = async () => {
        if (!user) { navigate('/login'); return; }
        try {
            if (inTbr) {
                await axios.delete(`${API}/tbr/${bookId}`, { headers: getAuthHeader() });
                setInTbr(false);
                showMsg('Removed from your TBR shelf.', 'info');
            } else {
                await axios.post(`${API}/tbr/${bookId}`, {}, { headers: getAuthHeader() });
                setInTbr(true);
                showMsg('Saved to your TBR shelf.', 'success');
            }
        } catch (e) {
            showMsg(e.response?.data?.detail || 'Could not update TBR.', 'error');
        }
    };

    const handleRentToCart = async () => {
        if (!user) { navigate('/login'); return; }
        try {
            await axios.post(`${API}/rentals/cart/items`, { book_id: bookId }, { headers: getAuthHeader() });
            window.dispatchEvent(new Event('rental-cart-updated'));
            const go = window.confirm(`Added to your cart (₹${price}). Go to cart now?`);
            if (go) navigate('/rental-cart');
        } catch (e) {
            showMsg(e.response?.data?.detail || 'Could not add to cart.', 'error');
        }
    };

    const openHoldModal = () => {
        if (!user) { navigate('/login'); return; }
        if (!canHold) { handleRentToCart(); return; }
        setRequestedDate(ymd(new Date()));
        setDeliveryOption('self_pickup');
        setDeliveryAddress(user?.address || '');
        setForChild(false);
        setHoldOpen(true);
    };

    const handleCtaClick = () => {
        if (!user) { navigate(`/signup?mode=rent&redirect=/book/${bookId}`); return; }
        if (canHold) openHoldModal();
        else handleRentToCart();
    };

    const handlePlaceHold = async () => {
        if (deliveryOption === 'paid_delivery' && !deliveryAddress.trim()) {
            showMsg('Please enter a delivery address.', 'error');
            return;
        }
        setPlacing(true);
        try {
            const finalDate = deliveryOption === 'paid_delivery'
                ? ymd(nextThursday(requestedDate ? new Date(requestedDate) : new Date()))
                : (requestedDate || ymd(new Date()));
            const payload = { book_id: bookId, delivery_option: deliveryOption, requested_date: finalDate };
            if (forChild) payload.for_child = true;
            if (deliveryOption === 'paid_delivery') payload.delivery_address = deliveryAddress.trim();
            const res = await axios.post(`${API}/holds`, payload, { headers: getAuthHeader() });
            const data = res.data || {};
            let text;
            if (data.is_queued) text = data.message || 'Added to your personal queue.';
            else if (data.is_coming_soon) text = data.message || `Hold placed! Available from ${data.available_from}.`;
            else {
                const pos = data.queue_position || 1;
                text = `Hold placed! You are #${pos} in queue.`;
                if (data.estimated_wait_weeks) text += ` Estimated wait: ~${data.estimated_wait_weeks} week(s).`;
            }
            if (data.loan_shortened_warning) text += `\n\n⚠️ ${data.loan_shortened_warning}`;
            setHoldOpen(false);
            showMsg(text, 'success');
        } catch (e) {
            showMsg(e.response?.data?.detail || 'Could not place hold.', 'error');
        } finally {
            setPlacing(false);
        }
    };

    const handleSubmitReview = async () => {
        if (!draft.trim()) return;
        setPosting(true);
        try {
            const res = await axios.post(`${API}/reviews`, { book_id: bookId, rating: draftStars, review_text: draft.trim() }, { headers: getAuthHeader() });
            setReviews([res.data, ...reviews]);
            setDraft('');
            setDraftStars(5);
            showMsg('Review posted — thanks!', 'success');
        } catch (e) {
            showMsg(e.response?.data?.detail || 'Could not post review.', 'error');
        } finally {
            setPosting(false);
        }
    };

    if (loading) {
        return <PublicLayout theme="archive"><div style={{ padding: '80px 48px', textAlign: 'center', fontStyle: 'italic', opacity: 0.7 }}>Loading book…</div></PublicLayout>;
    }
    if (!book) {
        return (
            <PublicLayout theme="archive">
                <div style={{ maxWidth: 1140, margin: '0 auto', padding: '80px 48px', textAlign: 'center' }}>
                    <h1 style={{ fontFamily: "'Libre Caslon Display', serif" }}>Book not found</h1>
                    <Link to="/catalog" style={{ color: 'var(--a3)', textDecoration: 'underline wavy 2px var(--a1)', textUnderlineOffset: 5 }}>← back to the catalog</Link>
                </div>
            </PublicLayout>
        );
    }

    const avgRating = reviews.length ? reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length : 0;
    const isReference = availability?.is_reference || availability?.base_loan_days === 0;
    let ctaLabel;
    if (!isAvailable) ctaLabel = user ? 'Join hold queue' : 'Notify me when available';
    else if (canHold) ctaLabel = 'Request to borrow';
    else ctaLabel = user ? `Add to cart — ₹${price}` : `Rent this book — ₹${price}`;

    const canReview = !!user;

    return (
        <PublicLayout theme="archive" grid="--a3" gridStrength={26}>
            <div className="hydlib-scallop" style={{ backgroundImage: 'radial-gradient(circle at 50% 0, var(--a3) 0 5px, transparent 6px)' }} />
            <div className="hydlib-pad" style={{ maxWidth: 1140, margin: '0 auto', padding: '56px 48px 72px' }}>
                <Link to="/catalog" style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--ink)', textDecoration: 'underline wavy 2px var(--a1)', textUnderlineOffset: 5 }}>← back to the catalog</Link>

                <div className="hydlib-cols" style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 56, alignItems: 'start', marginTop: 32 }}>
                    {/* LEFT — cover + CTAs */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 320, width: '100%', margin: '0 auto' }}>
                        <div style={{ position: 'relative', width: '100%', aspectRatio: '2 / 3', boxShadow: '6px 8px 0 color-mix(in oklch, var(--line) 25%, transparent)', background: 'var(--paper)', overflow: 'hidden' }} data-testid="book-cover">
                            {book.cover_image_url
                                ? <img src={book.cover_image_url} alt={book.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: "'Libre Caslon Display', serif", fontSize: 22, textAlign: 'center', color: 'var(--a1)' }}>{book.title}</div>}
                        </div>

                        {isAvailable && totalCount > 1 && (
                            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 1, textAlign: 'center', color: 'var(--a3)' }}>{availableCount} of {totalCount} available</div>
                        )}

                        {isReference ? (
                            <div data-testid="reference-only-notice" style={{ border: '2px solid var(--a1)', background: 'color-mix(in oklch, var(--a1) 10%, transparent)', padding: '16px 14px', textAlign: 'center' }}>
                                <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>📖 Reference only</div>
                                <div style={{ fontSize: 14, lineHeight: 1.55 }}>This book lives at the library — come in and read it anytime. It can&apos;t be borrowed or rented.</div>
                            </div>
                        ) : (
                        <button
                            onClick={handleCtaClick}
                            data-testid="book-cta"
                            className="hydlib-highlight"
                            style={{ cursor: 'pointer', border: 'none', textAlign: 'center', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 14, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--ink)', padding: 14, background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a2) 62%, var(--paper)) 5%, color-mix(in oklch, var(--a2) 46%, var(--paper)) 52%, color-mix(in oklch, var(--a2) 68%, var(--paper)) 94%, transparent 98.5%)` }}
                        >
                            {ctaLabel}
                        </button>
                        )}

                        {!isReference && isAvailable && !canHold && (
                            <div style={{ textAlign: 'center', fontSize: 14, marginTop: -6 }} data-testid="book-membership-nudge">
                                or become a member for ₹500/month —{' '}
                                <Link
                                    to={user ? '/my-library?plan=monthly' : '/signup'}
                                    style={{ color: 'var(--a3)', textDecoration: 'underline wavy 2px var(--a1)', textUnderlineOffset: 4 }}
                                    data-testid="book-membership-link"
                                >
                                    unlimited books, five at a time →
                                </Link>
                            </div>
                        )}

                        <button
                            onClick={handleTbr}
                            data-testid="book-tbr"
                            style={{ cursor: 'pointer', textAlign: 'center', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 13, letterSpacing: 1.5, textTransform: 'uppercase', border: '1px solid var(--line)', background: inTbr ? 'var(--a1)' : 'var(--paper)', color: inTbr ? 'var(--o1)' : 'var(--ink)', padding: 12 }}
                        >
                            {inTbr ? '♥ In your TBR' : '♡ Add to TBR'}
                        </button>

                        {isStaff && (
                            <div style={{ border: '1px dashed var(--a4)', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="staff-toolbar">
                                <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a4)' }}>Staff only</div>
                                <button onClick={() => setEditOpen(true)} data-testid="staff-edit-book-btn" style={{ cursor: 'pointer', border: '1px solid var(--line)', background: 'var(--a1)', color: 'var(--o1)', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', padding: '9px 12px' }}>✎ Edit book</button>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <Link to="/admin" style={{ flex: 1, textAlign: 'center', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', border: '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)', padding: '8px 10px', textDecoration: 'none' }}>Admin</Link>
                                    <button onClick={handleDeleteBook} data-testid="staff-delete-book-btn" style={{ flex: 1, cursor: 'pointer', border: '1px solid var(--a4)', background: 'var(--paper)', color: 'var(--a4)', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', padding: '8px 10px' }}>Delete</button>
                                </div>
                                {libCard && (
                                    <div style={{ borderTop: '1px dashed var(--line)', paddingTop: 8, fontSize: 12.5, lineHeight: 1.6 }} data-testid="staff-holds-info">
                                        {(libCard.current_borrowers || []).map((cb) => {
                                            const overdue = cb.due_date && new Date(cb.due_date) < new Date();
                                            return (
                                                <div key={cb.hold_id}>
                                                    📖 Out with <strong>{cb.user?.name}</strong>{cb.due_date ? <> — due <span style={{ color: overdue ? 'var(--a4)' : 'inherit', fontWeight: overdue ? 700 : 400 }}>{fmtDateLong(cb.due_date)}{overdue ? ' (overdue)' : ''}</span></> : ''}
                                                </div>
                                            );
                                        })}
                                        {(libCard.pending_holds || []).length > 0 && (
                                            <div style={{ marginTop: 4 }}>
                                                <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', opacity: 0.7 }}>Queue ({libCard.pending_holds.length})</div>
                                                {libCard.pending_holds.map((h, idx) => (
                                                    <div key={h.hold_id}>{idx + 1}. {h.user?.name} — {h.status}{h.requested_pickup_date ? ` · pickup ${fmtDateLong(h.requested_pickup_date)}` : ''}</div>
                                                ))}
                                            </div>
                                        )}
                                        {(libCard.current_borrowers || []).length === 0 && (libCard.pending_holds || []).length === 0 && (
                                            <div style={{ fontStyle: 'italic', opacity: 0.7 }}>No active holds on this book.</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {!isReference && isAvailable && canHold && (
                            <p style={{ fontFamily: "'Caveat', cursive", fontSize: 20, fontWeight: 600, color: 'var(--a1)', margin: 0, textAlign: 'center', transform: 'rotate(-2deg)' }}>counts as one of your five!</p>
                        )}
                        {!isReference && !isAvailable && availability?.earliest_available_date && (
                            <p style={{ fontFamily: "'Caveat', cursive", fontSize: 18, color: 'var(--a5)', textAlign: 'center', transform: 'rotate(-1.5deg)', margin: 0 }}>Earliest: {fmtDateLong(availability.earliest_available_date)}</p>
                        )}

                        {msg && (
                            <div style={{
                                padding: '10px 14px', fontSize: 13, whiteSpace: 'pre-line',
                                border: `1px solid var(${msgKind === 'error' ? '--a4' : msgKind === 'success' ? '--a3' : '--line'})`,
                                background: `color-mix(in oklch, var(${msgKind === 'error' ? '--a4' : msgKind === 'success' ? '--a3' : '--a1'}) 12%, transparent)`,
                            }} data-testid="book-msg">{msg}</div>
                        )}
                    </div>

                    {/* RIGHT — details + reviews */}
                    <div>
                        {book.categories?.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }} data-testid="book-categories">
                                {book.categories.map((cat, ci) => (
                                    <Link key={cat} to={`/catalog?category=${encodeURIComponent(cat)}`} style={{ display: 'inline-block', background: ci % 2 === 0 ? 'var(--a3)' : 'var(--a1)', color: ci % 2 === 0 ? 'var(--o3)' : 'var(--o1)', border: '1px solid var(--line)', fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', padding: '3px 10px', transform: `rotate(${ci % 2 === 0 ? -1.5 : 1}deg)`, textDecoration: 'none' }}>{cat}</Link>
                                ))}
                            </div>
                        )}
                        <h1 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 42, lineHeight: 1.1, margin: '0 0 8px' }}>{book.title}</h1>
                        {book.authors?.length > 0 && <div style={{ fontSize: 18, fontStyle: 'italic', marginBottom: 12 }}>by {book.authors.join(', ')}</div>}
                        {avgRating > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
                                <span style={{ color: 'var(--a2)', fontSize: 20, letterSpacing: 2 }}>{stars(avgRating)}</span>
                                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, opacity: 0.75 }}>{avgRating.toFixed(1)} · {reviews.length} review{reviews.length === 1 ? '' : 's'}</span>
                            </div>
                        )}
                        {(book.publisher || book.published_date || book.page_count || book.isbn) && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', opacity: 0.75, marginBottom: 28 }}>
                                {book.publisher && <span>{book.publisher}</span>}
                                {book.published_date && <span>· {book.published_date}</span>}
                                {book.page_count && <span>· {book.page_count} pages</span>}
                                {book.isbn && <span>· ISBN {book.isbn}</span>}
                            </div>
                        )}

                        {book.description && (
                            <>
                                <h2 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 26, margin: '0 0 12px' }}>Description</h2>
                                <div style={{ border: '1px solid var(--line)', background: 'var(--paper)', boxShadow: '5px 6px 0 color-mix(in oklch, var(--line) 18%, transparent)', padding: '20px 24px', marginBottom: 36 }} data-testid="book-description">
                                    <p style={{ fontSize: 16, lineHeight: 1.75, margin: 0, whiteSpace: 'pre-line' }}>{book.description}</p>
                                </div>
                            </>
                        )}

                        <h2 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 26, margin: '0 0 4px' }}>Reviews</h2>
                        <p style={{ fontFamily: "'Caveat', cursive", fontSize: 20, fontWeight: 600, color: 'var(--a1)', margin: '0 0 20px', transform: 'rotate(-1deg)' }}>what the neighbourhood thinks</p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 28 }}>
                            {reviews.length === 0 && <p style={{ fontStyle: 'italic', opacity: 0.7, fontSize: 15 }}>No reviews yet — be the first.</p>}
                            {reviews.map((r, i) => (
                                <div key={r.id || i} style={{ border: '1px solid var(--line)', background: 'var(--paper)', padding: '18px 20px' }} data-testid={`review-${i}`}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
                                        <span style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase' }}>{r.user_name || 'Reader'}</span>
                                        <span style={{ color: 'var(--a2)', fontSize: 15, letterSpacing: 2 }}>{stars(r.rating)}</span>
                                    </div>
                                    <div style={{ fontSize: 15, lineHeight: 1.65 }}>{r.review_text}</div>
                                </div>
                            ))}
                        </div>

                        {canReview ? (
                            <div style={{ border: '1px solid var(--line)', borderTop: '6px solid var(--a2)', background: 'var(--paper)', padding: '22px 24px' }}>
                                <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a2)', marginBottom: 12 }}>Leave a review</div>
                                <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                                    {[1, 2, 3, 4, 5].map(i => (
                                        <button key={i} onClick={() => setDraftStars(i)} data-testid={`star-${i}`} style={{ cursor: 'pointer', border: 'none', background: 'none', fontSize: 24, color: i <= draftStars ? 'var(--a2)' : 'color-mix(in oklch, var(--ink) 25%, transparent)', padding: '0 2px' }}>★</button>
                                    ))}
                                </div>
                                <textarea
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value)}
                                    placeholder="What did you think?"
                                    data-testid="review-text"
                                    style={{ width: '100%', minHeight: 80, border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--ink)', fontSize: 15, padding: '12px 14px', resize: 'vertical', fontFamily: 'inherit' }}
                                />
                                <button
                                    onClick={handleSubmitReview}
                                    disabled={posting || !draft.trim()}
                                    data-testid="review-submit"
                                    style={{ cursor: posting ? 'wait' : 'pointer', marginTop: 12, border: '1px solid var(--line)', background: 'var(--a2)', color: 'var(--o2)', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', padding: '10px 20px', opacity: posting || !draft.trim() ? 0.5 : 1 }}
                                >
                                    {posting ? 'Posting…' : 'Post review'}
                                </button>
                            </div>
                        ) : (
                            <p style={{ fontSize: 15, fontStyle: 'italic', opacity: 0.85 }}>
                                Want to leave a review? <Link to="/signup" style={{ color: 'var(--a3)' }}>Become a member</Link> or <Link to="/login" style={{ color: 'var(--a3)' }}>log in</Link>.
                            </p>
                        )}

                    </div>
                </div>

                {/* MORE LIKE THIS */}
                {similar.length > 0 && (
                    <div style={{ marginTop: 64 }} data-testid="more-like-this">
                        <h2 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 26, margin: '0 0 4px' }}>More like this</h2>
                        <p style={{ fontFamily: "'Caveat', cursive", fontSize: 20, fontWeight: 600, color: 'var(--a1)', margin: '0 0 20px', transform: 'rotate(-1deg)' }}>same shelves, same moods</p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 24 }}>
                            {similar.map(b => (
                                <Link key={b.id} to={`/book/${b.id}`} data-testid={`similar-book-${b.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                                    <div style={{ aspectRatio: '2 / 3', background: 'var(--paper)', border: '1px solid var(--line)', boxShadow: '4px 5px 0 color-mix(in oklch, var(--line) 20%, transparent)', overflow: 'hidden', position: 'relative', marginBottom: 10 }}>
                                        {b.cover_image_url
                                            ? <img src={b.cover_image_url} alt={b.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                            : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12, fontFamily: "'Libre Caslon Display', serif", fontSize: 16, textAlign: 'center', color: 'var(--a1)' }}>{b.title}</div>}
                                    </div>
                                    <div style={{ fontFamily: "'Libre Caslon Display', serif", fontSize: 15, lineHeight: 1.3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{b.title}</div>
                                    {b.authors?.[0] && <div style={{ fontSize: 12.5, fontStyle: 'italic', opacity: 0.75, marginTop: 2 }}>{b.authors[0]}</div>}
                                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginTop: 4, color: (b.available_copies ?? 0) > 0 ? 'var(--a3)' : 'var(--a5)' }}>{(b.available_copies ?? 0) > 0 ? 'Available' : 'On loan'}</div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* STAFF EDIT MODAL */}
            {editOpen && (
                <EditBookModal book={book} saving={saving} onSave={handleSaveBook} onClose={() => setEditOpen(false)} />
            )}

            {/* HOLD MODAL */}
            {holdOpen && (
                <HoldModal
                    onClose={() => setHoldOpen(false)}
                    onPlace={handlePlaceHold}
                    placing={placing}
                    requestedDate={requestedDate}
                    setRequestedDate={(d) => { setRequestedDate(d); fetchAvailability(d); }}
                    deliveryOption={deliveryOption}
                    setDeliveryOption={setDeliveryOption}
                    user={user}
                    forChild={forChild}
                    setForChild={setForChild}
                    deliveryAddress={deliveryAddress}
                    setDeliveryAddress={setDeliveryAddress}
                    book={book}
                    availability={availability}
                />
            )}
        </PublicLayout>
    );
}

/* Staff edit modal ------------------------------------------------------- */
function EditBookModal({ book, saving, onSave, onClose }) {
    const [form, setForm] = useState({
        title: book.title || '',
        authors: (book.authors || []).join(', '),
        isbn: book.isbn || '',
        publisher: book.publisher || '',
        published_date: book.published_date || '',
        page_count: book.page_count ?? '',
        description: book.description || '',
        categories: (book.categories || []).join(', '),
        cover_image_url: book.cover_image_url || '',
        total_copies: book.total_copies ?? 1,
        available_copies: book.available_copies ?? 0,
        rental_price: book.rental_price ?? '',
    });
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const inputStyle = { width: '100%', boxSizing: 'border-box', border: '1px solid var(--line)', background: 'var(--bg)', padding: '9px 12px', fontSize: 14, color: 'var(--ink)', fontFamily: 'inherit' };
    const labelStyle = { fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', opacity: 0.75, display: 'block', marginBottom: 4 };
    const field = (label, key, extra = {}) => (
        <div>
            <label style={labelStyle}>{label}</label>
            <input value={form[key]} onChange={set(key)} style={inputStyle} data-testid={`edit-book-${key}`} {...extra} />
        </div>
    );
    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'color-mix(in oklch, var(--ink) 45%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} data-testid="edit-book-modal">
            <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--paper)', border: '1px solid var(--line)', borderTop: '6px solid var(--a1)', boxShadow: '10px 12px 0 color-mix(in oklch, var(--line) 25%, transparent)', maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: '30px 32px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
                    <div>
                        <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)' }}>Staff · Edit book</div>
                        <div style={{ fontFamily: "'Libre Caslon Display', serif", fontSize: 24, marginTop: 4 }}>{book.title}</div>
                    </div>
                    <button onClick={onClose} data-testid="edit-book-close" style={{ cursor: 'pointer', border: 'none', background: 'none', fontSize: 22, color: 'var(--ink)' }}>✕</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {field('Title', 'title')}
                    {field('Authors (comma separated)', 'authors')}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        {field('ISBN', 'isbn')}
                        {field('Publisher', 'publisher')}
                        {field('Published date', 'published_date')}
                        {field('Pages', 'page_count', { type: 'number', min: 0 })}
                    </div>
                    {field('Categories (comma separated)', 'categories')}
                    {field('Cover image URL', 'cover_image_url')}
                    <div>
                        <label style={labelStyle}>Description</label>
                        <textarea value={form.description} onChange={set('description')} rows={4} style={{ ...inputStyle, resize: 'vertical' }} data-testid="edit-book-description" />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                        {field('Total copies', 'total_copies', { type: 'number', min: 1 })}
                        {field('Available copies', 'available_copies', { type: 'number', min: 0 })}
                        {field('Rental price ₹ (blank = default)', 'rental_price', { type: 'number', min: 0 })}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
                    <button onClick={() => onSave(form)} disabled={saving || !form.title.trim()} data-testid="edit-book-save" style={{ cursor: 'pointer', border: '1px solid var(--line)', background: 'var(--a1)', color: 'var(--o1)', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', padding: '10px 20px', opacity: saving || !form.title.trim() ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save changes'}</button>
                    <button onClick={onClose} style={{ cursor: 'pointer', border: '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)', fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', padding: '10px 20px' }}>Cancel</button>
                </div>
            </div>
        </div>
    );
}

/* Hold modal ------------------------------------------------------------ */
function HoldModal({ onClose, onPlace, placing, requestedDate, setRequestedDate, deliveryOption, setDeliveryOption, deliveryAddress, setDeliveryAddress, book, availability, user, forChild, setForChild }) {
    const today = ymd(new Date());
    const maxDate = ymd(new Date(Date.now() + 120 * 24 * 60 * 60 * 1000));

    return (
        <div
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, background: 'color-mix(in oklch, var(--ink) 45%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}
            data-testid="hold-modal-backdrop"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{ background: 'var(--paper)', border: '1px solid var(--line)', borderTop: '6px solid var(--a2)', boxShadow: '10px 12px 0 color-mix(in oklch, var(--line) 25%, transparent)', maxWidth: 520, width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: '32px 32px 28px' }}
                data-testid="hold-modal"
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 8 }}>
                    <div>
                        <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a2)' }}>Request to borrow</div>
                        <div style={{ fontFamily: "'Libre Caslon Display', serif", fontSize: 26, marginTop: 4 }}>{book.title}</div>
                    </div>
                    <button onClick={onClose} data-testid="hold-modal-close" style={{ cursor: 'pointer', border: 'none', background: 'none', fontSize: 22, color: 'var(--ink)' }}>✕</button>
                </div>

                {availability?.queue_position > 0 && (
                    <div style={{ padding: '10px 14px', margin: '12px 0', background: 'color-mix(in oklch, var(--a5) 15%, transparent)', border: '1px dashed var(--a5)', fontSize: 14 }}>
                        {availability.queue_position} {availability.queue_position === 1 ? 'person is' : 'people are'} ahead of you. Earliest available: {fmtDateLong(availability.earliest_available_date)}.
                    </div>
                )}

                {user?.child_membership_status === 'active' && (
                    <label data-testid="hold-for-child-toggle" style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, padding: '10px 12px', border: '1px dashed var(--a5)', cursor: 'pointer', fontSize: 14 }}>
                        <input type="checkbox" checked={forChild} onChange={(e) => setForChild(e.target.checked)} data-testid="hold-for-child-checkbox" />
                        <span>Reserve for <b>{user?.child_name || 'my child'}</b> (children's membership — kids' books only)</span>
                    </label>
                )}

                <div style={{ marginTop: 16 }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                        {deliveryOption === 'self_pickup' ? 'Preferred pickup date' : 'Delivery is on Thursdays — pick a nearby Thursday base'}
                        <input
                            type="date"
                            value={requestedDate}
                            min={availability?.earliest_available_date || today}
                            max={maxDate}
                            onChange={(e) => setRequestedDate(e.target.value)}
                            data-testid="hold-date"
                            style={{ border: '1px solid var(--line)', background: 'var(--bg)', padding: '10px 12px', fontSize: 15, color: 'var(--ink)', fontFamily: 'inherit' }}
                        />
                    </label>

                    <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <button onClick={() => setDeliveryOption('self_pickup')} data-testid="hold-pickup" style={{ cursor: 'pointer', textAlign: 'left', border: `2px solid ${deliveryOption === 'self_pickup' ? 'var(--a5)' : 'var(--line)'}`, background: 'var(--bg)', color: 'var(--ink)', padding: '12px 14px', fontFamily: 'inherit' }}>
                            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 1 }}>{deliveryOption === 'self_pickup' ? '●' : '○'} PICKUP BOX</div>
                            <div style={{ fontSize: 12, marginTop: 4, opacity: 0.85 }}>Free · Mon–Sat</div>
                        </button>
                        <button onClick={() => setDeliveryOption('paid_delivery')} data-testid="hold-delivery" style={{ cursor: 'pointer', textAlign: 'left', border: `2px solid ${deliveryOption === 'paid_delivery' ? 'var(--a5)' : 'var(--line)'}`, background: 'var(--bg)', color: 'var(--ink)', padding: '12px 14px', fontFamily: 'inherit' }}>
                            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 1 }}>{deliveryOption === 'paid_delivery' ? '●' : '○'} THURSDAY DELIVERY</div>
                            <div style={{ fontSize: 12, marginTop: 4, opacity: 0.85 }}>By distance · paid after</div>
                        </button>
                    </div>

                    {deliveryOption === 'paid_delivery' && (
                        <div style={{ marginTop: 10, fontSize: 13, opacity: 0.85 }} data-testid="delivery-thursday-hint">
                            Your delivery Thursday: <strong>{fmtDateLong(ymd(nextThursday(requestedDate ? new Date(requestedDate) : new Date())))}</strong> · same-day orders close at 9 AM
                        </div>
                    )}

                    {deliveryOption === 'paid_delivery' && (
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', marginTop: 16 }}>
                            Delivery address
                            <input
                                type="text"
                                value={deliveryAddress}
                                onChange={(e) => setDeliveryAddress(e.target.value)}
                                placeholder="Flat, street, area, pincode"
                                data-testid="hold-address"
                                style={{ border: '1px solid var(--line)', background: 'var(--bg)', padding: '10px 12px', fontSize: 15, color: 'var(--ink)', fontFamily: 'inherit' }}
                            />
                        </label>
                    )}

                    {availability?.loan_shortened_warning && (
                        <div style={{ marginTop: 16, padding: '10px 14px', border: '1px solid var(--a4)', background: 'color-mix(in oklch, var(--a4) 10%, transparent)', fontSize: 13 }}>
                            <strong>Shortened loan period</strong> — {availability.loan_shortened_warning}
                        </div>
                    )}

                    <button
                        onClick={onPlace}
                        disabled={placing}
                        data-testid="hold-place"
                        className="hydlib-highlight"
                        style={{ marginTop: 20, width: '100%', cursor: placing ? 'wait' : 'pointer', border: 'none', textAlign: 'center', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 14, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--ink)', padding: 14, background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a2) 62%, var(--paper)) 5%, color-mix(in oklch, var(--a2) 46%, var(--paper)) 52%, color-mix(in oklch, var(--a2) 68%, var(--paper)) 94%, transparent 98.5%)`, opacity: placing ? 0.7 : 1 }}
                    >
                        {placing ? 'Placing hold…' : 'Confirm hold'}
                    </button>
                </div>
            </div>
        </div>
    );
}
